import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { formatDistanceToNowStrict } from "date-fns";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  Popover,
  Tooltip,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { Badge } from "../Badge/Badge";
import { captureException } from "@sentry/react";
import { notify } from "../../feedback/SnackbarProvider";
import { arrivalsToToast } from "./newUnread";
import { getEventPresentation, isNavigable } from "./eventPresentation";
import {
  MARK_NOTIFICATIONS_READ,
  NOTIFICATION_ARTIFACT_URL,
  MUTED_NOTIFICATION_COUNT,
  NOTIFICATIONS_QUERY,
  NOTIFICATION_EVENT_PREFERENCES,
} from "./graphql";

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 60_000;

export interface NotificationBellProps {
  /** Host chrome styling — diligence's toolbar icons are white on a dark bar. */
  sx?: SxProps<Theme>;
  /**
   * Where a notification's subject lives, when the host app has such a route.
   * Receives the whole subject so the host can route by type — an async task id
   * is not an investment id, and routing it as one produces a dead link.
   * Omitted in data-collection, which has no guidelines screen.
   */
  onNavigate?: (subject: {
    eventType: string;
    subjectType: string;
    subjectId: number;
  }) => void;
  /**
   * Subject types THIS host can actually reach. Omit when it can reach all of
   * them (Sirius); pass the subset otherwise.
   *
   * Without it, a host that handles one subject type still renders a link for
   * every navigable event — data-collection has no investment or notes screen,
   * so "View guidelines" sat there doing nothing when clicked. A link that goes
   * nowhere is worse than no link: it reads as the feature being broken.
   */
  navigableSubjects?: readonly string[];
  /**
   * Opens the host's notification settings. The bell is where someone notices
   * they are getting too much or too little, so the fix belongs one click away.
   * Omitted where the host has no settings page.
   */
  onOpenSettings?: () => void;
}

interface NotificationRow {
  id: number;
  eventType: string;
  subjectType: string;
  subjectId: number;
  message: string;
  detail?: string | null;
  hasArtifact?: boolean;
  reason: string;
  createdAt: string;
  readAt?: string | null;
}

/** "3h ago" / "12d ago" — absolute date lives in the tooltip. */
function relativeTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return `${formatDistanceToNowStrict(date)} ago`;
}

/**
 * Can this host open this row? The event type says where it would go; the host
 * says whether it can go there.
 */
function canOpen(
  notification: { eventType: string; subjectId: number; subjectType: string },
  navigableSubjects?: readonly string[]
): boolean {
  if (!isNavigable(notification)) return false;
  return (
    !navigableSubjects || navigableSubjects.includes(notification.subjectType)
  );
}

function NotificationItem({
  notification,
  onNavigate,
  navigableSubjects,
  onRead,
}: {
  notification: NotificationRow;
  onNavigate?: NotificationBellProps["onNavigate"];
  navigableSubjects?: readonly string[];
  onRead: (id: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showReason, setShowReason] = useState(false);
  const [artifactState, setArtifactState] = useState<
    "idle" | "loading" | "expired" | "failed"
  >("idle");
  const [fetchArtifactUrl] = useLazyQuery(NOTIFICATION_ARTIFACT_URL, {
    // Always ask the server for a fresh signature; a cached one is a link that
    // works once and then silently stops.
    fetchPolicy: "no-cache",
    // Resolve with `error` rather than rejecting, so a failure cannot leave the
    // button stuck on "Preparing…" via an unhandled rejection.
    errorPolicy: "all",
  });
  const unread = !notification.readAt;
  const presentation = getEventPresentation(notification.eventType);
  const { Icon } = presentation;

  const toggle = () => {
    // Expanding IS reading it — the badge should not outlive the act of looking.
    // Bulk "mark all read" stays available in the header.
    if (!open && unread) onRead(notification.id);
    setOpen(o => !o);
  };

  return (
    <ListItem
      disablePadding
      sx={{ display: "block", px: 2, py: 1.25, alignItems: "flex-start" }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${notification.message}. ${open ? "Collapse" : "Expand"} for details`}
        onClick={toggle}
        // A div with onClick is unreachable by keyboard; this row hides the
        // detail, the reason, the link and the download behind it.
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            toggle();
          }
        }}
        sx={{
          display: "flex",
          gap: 1,
          cursor: "pointer",
          "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" },
        }}
      >
        {/* Type icon leads the row so a mixed list scans by kind; unread rides on
            it as a dot rather than occupying a second column. */}
        <Box sx={{ mt: 0.25, flex: "0 0 auto" }}>
          <Badge dot={unread}>
            <Icon fontSize="small" color={presentation.color} />
          </Badge>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Weight stays constant: the dot carries unread, and re-weighting text
              on read reflows the row under the cursor. */}
          <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
            {notification.message}
          </Typography>
          <Tooltip title={new Date(notification.createdAt).toLocaleString()}>
            <Typography variant="caption" color="text.secondary">
              {relativeTime(notification.createdAt)}
            </Typography>
          </Tooltip>
        </Box>
        {open ? (
          <ExpandLessIcon fontSize="small" color="action" />
        ) : (
          <ExpandMoreIcon fontSize="small" color="action" />
        )}
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ pl: 2, pt: 1 }}>
          {notification.detail && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {notification.detail}
            </Typography>
          )}

          {notification.hasArtifact && (
            <Box sx={{ mb: 1 }}>
              <Link
                component="button"
                type="button"
                variant="body2"
                disabled={artifactState === "loading"}
                onClick={async () => {
                  setArtifactState("loading");
                  const { data, error } = await fetchArtifactUrl({
                    variables: { id: notification.id },
                  });
                  // A failed request is not an expired file. Telling someone to
                  // "run it again" because their token lapsed would be wrong, and
                  // it would bury the real error.
                  if (error) {
                    setArtifactState("failed");
                    return;
                  }
                  const url = data?.notificationArtifactUrl;
                  // Null from a successful call means the object is gone from its
                  // (temporary) bucket — say so rather than opening a 404.
                  if (!url) {
                    setArtifactState("expired");
                    return;
                  }
                  setArtifactState("idle");
                  window.open(url, "_blank", "noopener");
                }}
              >
                {artifactState === "loading" ? "Preparing…" : "Download"}
              </Link>
              {artifactState === "failed" && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block" }}
                >
                  Couldn&apos;t prepare the download. Try again.
                </Typography>
              )}
              {artifactState === "expired" && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  This file is no longer available — reports are kept
                  temporarily. Run it again to get a fresh copy.
                </Typography>
              )}
            </Box>
          )}

          {onNavigate && canOpen(notification, navigableSubjects) && (
            <Link
              component="button"
              type="button"
              variant="body2"
              onClick={() =>
                onNavigate({
                  eventType: notification.eventType,
                  subjectType: notification.subjectType,
                  subjectId: notification.subjectId,
                })
              }
              sx={{ display: "block", mb: 1 }}
            >
              {presentation.linkLabel}
            </Link>
          )}

          {/* Kept behind a click: every row would otherwise repeat the same
              sentence, and the reason only matters when someone asks. */}
          <Button
            size="small"
            variant="text"
            onClick={() => setShowReason(s => !s)}
            sx={{ px: 0, minWidth: 0, textTransform: "none" }}
          >
            Why am I seeing this?
          </Button>
          <Collapse in={showReason} unmountOnExit>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", pt: 0.5 }}
            >
              {notification.reason}
            </Typography>
          </Collapse>
        </Box>
      </Collapse>
    </ListItem>
  );
}

export function NotificationBell({
  sx,
  onNavigate,
  navigableSubjects,
  onOpenSettings,
}: NotificationBellProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { data, refetch } = useQuery(NOTIFICATIONS_QUERY, {
    variables: { limit: PAGE_SIZE },
    pollInterval: POLL_INTERVAL_MS,
    // A backgrounded tab does not need fresh notifications; without this, every
    // open tab keeps polling forever.
    skipPollAttempt: () => document.visibilityState === "hidden",
  });
  const [markRead] = useMutation(MARK_NOTIFICATIONS_READ);

  // Whether an arrival interrupts is the user's call, per kind of event — see
  // their notification settings. Fetched once; toasting is not worth a poll.
  const { data: preferenceData } = useQuery(NOTIFICATION_EVENT_PREFERENCES);
  const { data: mutedData } = useQuery(MUTED_NOTIFICATION_COUNT);
  const mutedCount = mutedData?.mutedNotificationCount ?? 0;
  const toastEnabled = useMemo(
    () =>
      new Set(
        (preferenceData?.notificationEventPreferences ?? [])
          .filter(p => p.toast)
          .map(p => p.eventType as string)
      ),
    [preferenceData]
  );

  const notifications: NotificationRow[] = useMemo(
    () => data?.notifications ?? [],
    [data]
  );
  const unreadIds = useMemo(
    () => notifications.filter(n => !n.readAt).map(n => n.id),
    [notifications]
  );

  // Toast anything that arrived since the last poll. The badge is the passive
  // indication; this is the active one, for a user who is not looking at the
  // header. Deliberately driven off the existing poll rather than a websocket:
  // at one notification per portfolio per month, sub-60s delivery buys nothing
  // and would cost a server-side pubsub across the worker/API process boundary.
  const seenIds = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (!data) return;
    // Everything still lands in the bell; only the interrupting part is optional.
    const fresh = arrivalsToToast(seenIds.current, notifications, toastEnabled);
    seenIds.current = new Set(notifications.map(n => n.id));
    if (!fresh.length) return;

    if (fresh.length === 1) {
      const [only] = fresh;
      notify({
        message: only.message,
        description: only.detail ?? only.reason,
        severity: getEventPresentation(only.eventType).severity,
        // Same notification twice (a refetch racing a poll) replaces rather than stacks.
        key: `notification-${only.id}`,
        onClick:
          onNavigate && canOpen(only, navigableSubjects)
            ? () =>
                onNavigate({
                  eventType: only.eventType,
                  subjectType: only.subjectType,
                  subjectId: only.subjectId,
                })
            : undefined,
      });
      return;
    }
    notify({
      message: `${fresh.length} new notifications`,
      description: "Open the bell to review them.",
      severity: "info",
      key: "notifications-batch",
    });
  }, [data, notifications, onNavigate, navigableSubjects, toastEnabled]);

  /**
   * Marking read is called from click handlers, so a rejected promise would
   * otherwise be an unhandled rejection and the badge would simply not clear —
   * indistinguishable from "nothing was unread". Surfacing it costs one line and
   * makes the failure legible; the notification itself is not lost either way.
   */
  const markReadIds = useCallback(
    async (ids: number[]) => {
      if (!ids.length) return;
      try {
        await markRead({ variables: { ids } });
        await refetch();
      } catch (error) {
        captureException(error);
        // Same channel the arrival toasts use, so there is no second
        // notification surface to maintain for one error case.
        notify({
          message: "Couldn't mark that as read",
          description: "It is still in your list — try again.",
          severity: "error",
          key: "notification-mark-read-failed",
        });
      }
    },
    [markRead, refetch]
  );

  const markOneRead = useCallback(
    (id: number) => markReadIds([id]),
    [markReadIds]
  );

  const markAllRead = useCallback(
    () => markReadIds(unreadIds),
    [markReadIds, unreadIds]
  );

  const label = unreadIds.length
    ? `Notifications, ${unreadIds.length} unread`
    : "Notifications";

  return (
    <>
      <Tooltip title="Notifications" arrow>
        <IconButton
          aria-label={label}
          onClick={event => setAnchorEl(event.currentTarget)}
          size="large"
          color="inherit"
          sx={sx}
        >
          <Badge count={unreadIds.length} overflowCount={PAGE_SIZE}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        // MUI's modal scroll-lock hides the page scrollbar while the popover is
        // open, which shifts the whole layout on every bell click. The popover
        // is anchored, not a dialog — it has no reason to own page scrolling.
        disableScrollLock
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 400 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2,
              py: 1.5,
            }}
          >
            <Typography variant="subtitle2">
              Notifications
              {unreadIds.length > 0 && ` · ${unreadIds.length} unread`}
            </Typography>
            <Box>
              {unreadIds.length > 0 && (
                <Button
                  size="small"
                  onClick={markAllRead}
                  sx={{ textTransform: "none" }}
                >
                  Mark all read
                </Button>
              )}
              {onOpenSettings && (
                <Tooltip title="Notification settings">
                  <IconButton
                    size="small"
                    aria-label="Notification settings"
                    onClick={() => {
                      setAnchorEl(null);
                      onOpenSettings();
                    }}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
          <Divider />

          {/* Scrolls rather than grows: the query is capped at PAGE_SIZE, so the
              worst case is a bounded list, not an unbounded popover. */}
          <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ px: 2, py: 3 }}
              >
                Nothing yet.
              </Typography>
            ) : (
              <List disablePadding>
                {notifications.map((notification, index) => (
                  <Box key={notification.id}>
                    {index > 0 && <Divider component="li" />}
                    <NotificationItem
                      notification={notification}
                      onNavigate={onNavigate}
                      navigableSubjects={navigableSubjects}
                      onRead={markOneRead}
                    />
                  </Box>
                ))}
              </List>
            )}
          </Box>

          {mutedCount > 0 && (
            <>
              <Divider />
              {/* Muting hides, it does not discard — saying so is the difference
                  between a filter and a silent drop. */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 2, py: 1 }}
              >
                {mutedCount} hidden by your notification settings.
              </Typography>
            </>
          )}

          {notifications.length >= PAGE_SIZE && (
            <>
              <Divider />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", px: 2, py: 1 }}
              >
                Showing the {PAGE_SIZE} most recent.
              </Typography>
            </>
          )}
        </Box>
      </Popover>
    </>
  );
}

export default NotificationBell;
